import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMessage } from "../useMessage";
import { useAppDispatch, setCredentials } from "../../store";
import type { AuthState } from "../../types";

type ApiResult<T> =
  | {
      unwrap(): Promise<T>;
    }
  | Promise<T>
  | T;

export function useAuthComplete() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const message = useMessage();

  return useCallback(
    (value: ApiResult<AuthState>, event?: React.FormEvent) => {
      event?.preventDefault();

      const dispatchAuth = (auth: AuthState) => {
        if (auth.user && auth.access_token) {
          dispatch(
            setCredentials({
              user: auth.user,
              access_token: auth.access_token,
            }),
          );
        }

        navigate("/customers", { replace: true });

        // Show success message
        message.success("Successfully signed in!");
      };

      const handleError = (error: any) => {
        console.error("Authentication error:", error);
        message.error(
          error?.data?.message ||
            error?.message ||
            "Authentication failed. Please try again.",
        );
      };

      if (value && typeof value === "object" && "unwrap" in value) {
        value.unwrap().then(dispatchAuth).catch(handleError);
      } else if (value && typeof value === "object" && "then" in value) {
        value.then(dispatchAuth).catch(handleError);
      } else {
        dispatchAuth(value as AuthState);
      }
    },
    [dispatch, navigate, message],
  );
}
